import {ApolloError} from '@apollo/client';

import AnnotateDomainSequencesSettingsModal from 'antibodies/domain-annotation/AnnotateDomainSequencesSettingsModal';
import {
  AnnotationSubmitDecision,
  AnnotationSubmitDecisionType,
  DomainSequenceType,
} from 'antibodies/domain-annotation/DomainAnnotationTypes';
import isDomainSequenceAnnotationEnabled from 'antibodies/domain-annotation/isDomainSequenceAnnotationEnabled';
import {AntibodyComponentSchemaInterfaceApiId} from 'antibodies/schema-configuration/AntibodySchemaInterfaceConstants';
import {TagSchema} from 'constants/RegistryConstants';
import {TagSchemaItemType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {RowSubmissionRange} from 'notebook/structured-tables/action-functions/makeSubmitRowActionFunction';
import {StructuredTableRow} from 'notebook/structured-tables/StructuredTableConstants';
import getTagSchemaInterfaceIds from 'schemas/util/getTagSchemaInterfaceIds';
import EntryStore from 'stores/EntryStore';
import {ZINDEX_MODAL} from 'styles/StyleConstants';
import * as LoadingScreen from 'ui-library/LoadingScreen';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';
import * as logging from 'util/Logging';

const REGISTRATION_ANNOTATION_INFO_BANNER_TEXT = 'Sequence annotation will run after registration';

const DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE: Record<
  DomainSequenceType,
  AntibodyComponentSchemaInterfaceApiId
> = {
  [DomainSequenceType.AA]: AntibodyComponentSchemaInterfaceApiId.AA_DOMAIN_ID,
  [DomainSequenceType.DNA]: AntibodyComponentSchemaInterfaceApiId.DNA_DOMAIN_ID,
};

/** Domain SSIs can only be implemented by AA and DNA sequence schemas. */
function getDomainSequenceType(itemType: TagSchemaItemType): DomainSequenceType | null {
  switch (itemType) {
    case TagSchemaItemType.AA_SEQUENCE:
      return DomainSequenceType.AA;
    case TagSchemaItemType.DNA_SEQUENCE:
      return DomainSequenceType.DNA;
    default:
      return null;
  }
}

/**
 * Query failures resolve to false so that registration proceeds without annotation, which is opt-in and only
 * runs after registration.
 */
async function implementsSchemaInterface(tagSchemaId: string, interfaceId: string): Promise<boolean> {
  try {
    return (await getTagSchemaInterfaceIds(tagSchemaId)).has(interfaceId);
  } catch (error) {
    if (!(error instanceof ApolloError)) {
      throw error;
    }
    logging.warn('Failed to load schema interfaces for domain sequence annotation', {
      extra: {tagSchemaId, error},
    });
    return false;
  }
}

function getRegistrationAnnotationSettingsModalTitle(sequenceType: DomainSequenceType): string {
  return `Register ${sequenceType} domain entities`;
}

function getEntryFolderId(entryId: string): string | null {
  if (!APIUtil.isEntry(entryId) || !EntryStore.inStore(entryId)) {
    return null;
  }
  return EntryStore.getEditorState(entryId).entry.folder?.api_identifier ?? null;
}

/**
 * Prompts for sequence annotation settings when registering into an AA or DNA domain schema. Must be called
 * before registration so that the unregistered rows can be snapshotted.
 *
 * Must also be called before the shared submit flow opens its validation bypass or data modifications
 * dialogs. Those Bootstrap dialogs stay open while registration runs, and Bootstrap's focus trap would
 * keep this Mantine modal's inputs from holding focus.
 *
 * @param selection - The rows being submitted, or undefined when submitting the whole table.
 */
export default async function promptForSequenceAnnotationConfig({
  tagSchema,
  rows,
  entryId,
  selection,
}: {
  tagSchema: Pick<TagSchema, 'id' | 'itemType'>;
  rows: Array<StructuredTableRow>;
  entryId: string;
  selection?: RowSubmissionRange;
}): Promise<AnnotationSubmitDecision> {
  if (!isDomainSequenceAnnotationEnabled()) {
    return {type: AnnotationSubmitDecisionType.SKIP_ANNOTATION};
  }
  const sequenceType = getDomainSequenceType(tagSchema.itemType);
  if (sequenceType == null) {
    return {type: AnnotationSubmitDecisionType.SKIP_ANNOTATION};
  }
  const hasDomainInterface = await implementsSchemaInterface(
    tagSchema.id,
    DOMAIN_INTERFACE_ID_BY_SEQUENCE_TYPE[sequenceType]
  );
  if (!hasDomainInterface) {
    return {type: AnnotationSubmitDecisionType.SKIP_ANNOTATION};
  }

  const config = await LoadingScreen.withLoadingScreenHidden(() =>
    runModal(
      AnnotateDomainSequencesSettingsModal,
      {
        sequenceType,
        initialFolderId: getEntryFolderId(entryId),
        submitButtonText: 'Register',
        infoBannerText: REGISTRATION_ANNOTATION_INFO_BANNER_TEXT,
      },
      {
        title: getRegistrationAnnotationSettingsModalTitle(sequenceType),
        size: 'lg',
        zIndex: ZINDEX_MODAL,
      }
    )
  );
  if (config === undefined) {
    return {type: AnnotationSubmitDecisionType.CANCEL};
  }
  if (config == null) {
    return {type: AnnotationSubmitDecisionType.SKIP_ANNOTATION};
  }
  const submittedRows = selection ? rows.slice(selection.start, selection.end) : rows;
  return {
    type: AnnotationSubmitDecisionType.ANNOTATE,
    config,
    unregisteredRowIds: submittedRows.filter((row) => !row.isFinalized).map((row) => row.rowId),
  };
}
